import { API, graphqlOperation } from 'aws-amplify';
import React from 'react';
import type { GraphQLResult } from '@aws-amplify/api';
import type { GraphQLError } from 'graphql';

interface GraphQLErrorEx extends GraphQLError {
  path: string[];
  data?: unknown;
  errorType: string;
  errorInfo: {
    code: number;
    message: string;
  };
  locations: {
    line: number;
    column: number;
    sourceName?: unknown;
  }[];
  message: string;
}

export type GraphQLResultEx2<T = Record<string, unknown>> =
  | {
      data: T;
      errors?: null;
      extensions?: {
        [key: string]: unknown;
      };
    }
  | {
      data?: null;
      errors: GraphQLErrorEx[];
      extensions?: {
        [key: string]: unknown;
      };
    };

interface GraphQLResultEx<T extends object> extends GraphQLResult {
  data?: T;
  errors?: GraphQLErrorEx[];
  extensions?: {
    [key: string]: unknown;
  };
}

/**
 * @deprecated use useAmplifyQuery instead
 */
export function useAmplifyFetcher<
  TData extends object,
  TVariables extends object,
>(): {
  loading: boolean;
  data: GraphQLResultEx<TData> | null;
  fetcher: (
    query: string,
    variables: TVariables
  ) => Promise<GraphQLResultEx<TData>>;
} {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<GraphQLResultEx<TData> | null>(null);

  const fetcher = React.useCallback(
    (query: string, variables: TVariables): Promise<GraphQLResultEx<TData>> => {
      setLoading(true);

      return (
        API.graphql(graphqlOperation(query, variables)) as Promise<
          GraphQLResultEx<object>
        >
      )
        .then((result: GraphQLResultEx<object>): GraphQLResultEx<object> => {
          setData(result as unknown as GraphQLResultEx<TData>);
          return result;
        })
        .catch((error) => {
          setData(error as GraphQLResultEx<TData>);
          return error;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  return {
    fetcher,
    data,
    loading,
  };
}

export function useAmplifyGraphql<TData, TVariables>() {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<GraphQLResultEx2<TData> | null>(
    null
  );

  const fetcher = React.useCallback((query: string, variables: TVariables) => {
    setLoading(true);

    return (
      API.graphql(graphqlOperation(query, variables ?? {})) as Promise<
        GraphQLResultEx2<TData>
      >
    )
      .then((response) => {
        setResult(response);
        return {
          response,
        };
      })
      .catch((response: GraphQLResultEx2<TData>) => {
        setResult(response);
        return {
          response,
        };
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return {
    fetcher,
    loading,
    result,
  };
}
